import { useHistory } from 'react-router-dom';
import URLS from '@/URLS';
import {
  failGetConfig,
  sendPostMessageToParent,
} from '@functions/trafficController';
import useEc2Fetch from '@hooks/SplashScreen/useEc2Fetch';
import { useEffect } from 'react';
import useSetupApp from '@hooks/config/useSetupApp';
import { getConfigRedirect } from '@hooks/useConfigRedirect';
import { useStoreAppData } from '@state/useGlobalState';
import { useStoreSiccMember } from '@state/useStoreSiccMember';
import { useStoreCrmMember } from '@state/useStoreCrmMember';
import { useStoreFirstCredential } from '@state/useStoreFirstCredential';
import useCheckValidStore from './useCheckValidStore';
import { useStoreOutlet } from '@state/useStoreOutlet';
import { useGeneralContext } from '@context/GeneralContext';
import {
  getDB,
  replicationMiniappReq,
  replicationStoreLog,
} from '@/InitiateRxdb';
import { useClientState } from '@state/useClientState';
import useCheckSalesType from '@hooks/useCheckSalesType';
import useCheckOpenCloseStore from '@hooks/useCheckOpenCloseStore';
import { useStoreOrderType } from '@state/useStoreOrderType';
import useRxdbStoreLog from '@hooks/useRxdbStoreLog';
import useHITApi from '@hooks/useHITApi';
import { useMiniappConfirm } from '@state/useMiniappConfirm';

export default function useFetchData(theme, setConfig) {
  const { credentials } = useStoreFirstCredential((state) => ({
    credentials: state.credentials,
  }));

  const { updateOutlet } = useStoreOutlet((state) => ({
    updateOutlet: state.updateOutlet,
  }));
  const { getClient } = useClientState((state) => ({
    getClient: state.getClient,
  }));

  const { showGreetingModal } = useGeneralContext();

  const dataConfig = useEc2Fetch();

  const restrictedUrls = [
    URLS.SESSION_EXPIRED,
    URLS.INVALID_TOKEN,
    URLS.MINIAPP_FAILURE,
    URLS.CANNOT_SICC,
    URLS.OUTLET_CLOSE,
    URLS.CONNECTION_FAILURE,
    URLS.BROWSER_NOT_SUPPORTED,
  ];

  const history = useHistory();
  const { updateAppData, appData } = useStoreAppData((state) => ({
    updateAppData: state.updateAppData,
    appData: state.appData,
  }));
  // inisiasi setupapp untuk menyimpan config
  // didalam state dan localStorage
  useSetupApp();
  ///hook check valid store
  const checkValidStore = useCheckValidStore();

  // Add hooks needed for redirect logic
  const checkSalesType = useCheckSalesType();
  const [OrderTypeNewData] = useCheckOpenCloseStore();
  const { order_type } = useStoreOrderType((state) => ({
    order_type: state.order_type,
  }));
  const [subscribeSplashscreen] = useRxdbStoreLog(credentials.miniapp);
  const hitAPI = useHITApi();
  const { updateMiniappConfirmId } = useMiniappConfirm((state) => ({
    updateMiniappConfirmId: state.updateMiniappConfirmId,
  }));
  // State for redirect URL

  const { updateSiccMember } = useStoreSiccMember((state) => ({
    updateSiccMember: state.updateSiccMember,
  }));

  const { updateCrmMember } = useStoreCrmMember((state) => ({
    updateCrmMember: state.updateCrmMember,
  }));

  const directPage = async () => {
    // Get redirect URL using the function instead of hook
    const redirectUrl = await getConfigRedirect(
      checkValidStore,
      credentials,
      { ...appData, config: dataConfig.config },
      OrderTypeNewData,
      order_type,
      checkSalesType,
      subscribeSplashscreen,
      hitAPI,
      updateMiniappConfirmId,
    );
    if (
      dataConfig?.product &&
      Array.isArray(dataConfig?.product) &&
      dataConfig.product?.length > 0
    ) {
      // redirectURL bisa null, undefined, atau string
      if (redirectUrl) {
        setTimeout(() => {
          if (
            dataConfig.config?.popupUrl &&
            !restrictedUrls.includes(redirectUrl)
          ) {
            showGreetingModal();
          }
          sendPostMessageToParent('path', redirectUrl);
          history.replace(redirectUrl);
        }, 2000);
      }
    } else {
      updateOutlet(dataConfig);
      const failConfig = await failGetConfig(dataConfig);
      if (failConfig) {
        sendPostMessageToParent('path', failConfig);
        history.replace(failConfig);
      }
    }
  };

  const nextSplahscreen = () => {
    let validTable = true;
    //custom gak ada tetep jalan normal
    ///custom off cek tablename ada gak, gak ada error, ada lanjut
    ///qrstore gak perlu pengecekan custom table
    if (dataConfig.id) {
      if (
        dataConfig.config.custom_table &&
        dataConfig.config.custom_table === 'off' &&
        !dataConfig.config.tableName
      ) {
        validTable = false;
      }
      if (dataConfig.config.use_sicc_member) updateSiccMember(credentials.sicc);
      // CRM-scan attribution is independent of the SICC membership gating:
      // capture crm_member_uid whenever it rode in on the entry URL so it can
      // be attached to the transaction at checkout (FE-Lambda flags is_order_np
      // only when it is present AND type_bill === 'payment_order').
      updateCrmMember(credentials.crm_member_uid || null);
      if (dataConfig.config.use_sicc_member && !dataConfig.config?.tableID)
        validTable = true;
      if (dataConfig.config.use_sicc_member && !credentials.sicc) {
        sendPostMessageToParent('path', URLS.CANNOT_SICC);
        history.replace(URLS.CANNOT_SICC);
      } else {
        if (!validTable) {
          sendPostMessageToParent('path', URLS.CONNECTION_FAILURE);
          history.replace(URLS.CONNECTION_FAILURE);
        }
        if (dataConfig.config.maintenance_mode) {
          if (dataConfig.config.order_method === 'payment_order') {
            sendPostMessageToParent('path', URLS.CANNOT_PAYMENT);
            history.replace(URLS.CANNOT_PAYMENT);
          } else {
            if (checkValidStore) directPage();
          }
        } else {
          if (checkValidStore) directPage();
        }
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Digunakan untuk mencegah error ketika pertama kali running
      // Gunakan untuk mencegah fetchdata berjalan jika dataConfig masih null/undefined
      // Mencegah terjadiinya error pada konsol akibat dataConfig null/undefined
      if (!dataConfig) {
        // Jika dataConfig belum terisi return
        // jika dataConfig sudah terisi kodingan ini akan dihiraukan
        return;
      }

      if (!dataConfig.id) {
        updateOutlet(dataConfig);
        const failConfig = await failGetConfig(dataConfig);
        if (failConfig) {
          sendPostMessageToParent('path', failConfig);
          history.replace(failConfig);
        }
      }

      theme(dataConfig.config);
      setConfig(dataConfig.config);
      updateAppData(dataConfig);
    };

    fetchData();
  }, [dataConfig, checkValidStore, credentials, appData, order_type]);

  useEffect(() => {
    const initializeDB = async () => {
      const db = await getDB();
      replicationStoreLog(db, dataConfig?.config?.storeUUID, getClient());
      replicationMiniappReq(db, dataConfig?.config?.storeUUID, getClient());
    };
    if (dataConfig && Object.keys(getClient()).length > 0) {
      initializeDB();
    }
  }, [getClient(), dataConfig]);

  return [dataConfig, nextSplahscreen, credentials];
}
